export type HomeRiVariant = 'control' | 'interactive_v1' | 'adaptive_v2';

export type HomeRiExperimentConfig = {
  enabled: boolean;
  rolloutPct: number;
  minImpressionsPerVariant: number;
  minLiftPct: number;
  minConfidence: number;
  forcedVariant: HomeRiVariant | null;
  updatedAt: string;
  lastAutoPromotedAt?: string;
};

type HomeRiVariantStats = {
  impressions: number;
  interactions: number;
  completions: number;
  upgradeClicks: number;
};

type HomeRiExperimentStats = Record<HomeRiVariant, HomeRiVariantStats>;

export type HomeRiVariantHealth = {
  variant: HomeRiVariant;
  impressions: number;
  interactions: number;
  completions: number;
  upgradeClicks: number;
  interactionRatePct: number;
  completionRatePct: number;
  upgradeRatePct: number;
};

export type HomeRiPromotionHealth = {
  variant: 'interactive_v1' | 'adaptive_v2';
  liftPct: number;
  confidence: number;
  sampleReady: boolean;
  eligibleForPromotion: boolean;
};

export type HomeRiExperimentHealth = {
  totals: {
    impressions: number;
    interactions: number;
    completions: number;
    upgradeClicks: number;
  };
  variants: Record<HomeRiVariant, HomeRiVariantHealth>;
  promotion: HomeRiPromotionHealth[];
};

export type HomeRiRolloutRecommendation = {
  action: 'start' | 'ramp' | 'hold' | 'rollback';
  targetRolloutPct: number;
  reason: string;
};

export const HOME_RI_EXPERIMENT_CONFIG_KEY = 'tension_home_ri_experiment_config_v1';
export const HOME_RI_VARIANT_KEY = 'tension_home_ri_variant_v1';
export const HOME_RI_EXPERIMENT_STATS_KEY = 'tension_home_ri_experiment_stats_v1';
export const HOME_RI_ROLLOUT_LAST_CHANGE_AT_KEY = 'tension_home_ri_rollout_last_change_at_v1';
export const HOME_RI_ROLLOUT_COOLDOWN_MS = 60 * 60 * 1000;
const HOME_RI_ASSIGNMENT_LOG_PREFIX = 'tension_ri_assignment_logged:';

export const DEFAULT_HOME_RI_EXPERIMENT_CONFIG: HomeRiExperimentConfig = {
  enabled: false,
  rolloutPct: 5,
  minImpressionsPerVariant: 30,
  minLiftPct: 3,
  minConfidence: 70,
  forcedVariant: null,
  updatedAt: new Date().toISOString(),
};

function defaultStats(): HomeRiExperimentStats {
  return {
    control: { impressions: 0, interactions: 0, completions: 0, upgradeClicks: 0 },
    interactive_v1: { impressions: 0, interactions: 0, completions: 0, upgradeClicks: 0 },
    adaptive_v2: { impressions: 0, interactions: 0, completions: 0, upgradeClicks: 0 },
  };
}

function normalizedBucket(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % 10000;
  }
  return Math.abs(hash) % 100;
}

export function readHomeRiExperimentConfig(): HomeRiExperimentConfig {
  try {
    const raw = localStorage.getItem(HOME_RI_EXPERIMENT_CONFIG_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return DEFAULT_HOME_RI_EXPERIMENT_CONFIG;
    return {
      enabled: Boolean(parsed.enabled),
      rolloutPct: Math.max(0, Math.min(100, Number(parsed.rolloutPct || 0))),
      minImpressionsPerVariant: Math.max(10, Math.min(1000, Number(parsed.minImpressionsPerVariant || 30))),
      minLiftPct: Math.max(0.5, Math.min(25, Number(parsed.minLiftPct || 3))),
      minConfidence: Math.max(40, Math.min(98, Number(parsed.minConfidence || 70))),
      forcedVariant:
        parsed.forcedVariant === 'control' ||
        parsed.forcedVariant === 'interactive_v1' ||
        parsed.forcedVariant === 'adaptive_v2'
          ? parsed.forcedVariant
          : null,
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date().toISOString(),
      lastAutoPromotedAt: typeof parsed.lastAutoPromotedAt === 'string' ? parsed.lastAutoPromotedAt : undefined,
    };
  } catch {
    return DEFAULT_HOME_RI_EXPERIMENT_CONFIG;
  }
}

export function writeHomeRiExperimentConfig(config: HomeRiExperimentConfig): void {
  try {
    localStorage.setItem(HOME_RI_EXPERIMENT_CONFIG_KEY, JSON.stringify(config));
  } catch {
    // noop
  }
}

export function readHomeRiExperimentStats(): HomeRiExperimentStats {
  try {
    const raw = localStorage.getItem(HOME_RI_EXPERIMENT_STATS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return defaultStats();
    const base = defaultStats();
    (Object.keys(base) as HomeRiVariant[]).forEach((variant) => {
      const candidate = parsed[variant] || {};
      base[variant] = {
        impressions: Number(candidate.impressions || 0),
        interactions: Number(candidate.interactions || 0),
        completions: Number(candidate.completions || 0),
        upgradeClicks: Number(candidate.upgradeClicks || 0),
      };
    });
    return base;
  } catch {
    return defaultStats();
  }
}

function writeHomeRiExperimentStats(stats: HomeRiExperimentStats): void {
  try {
    localStorage.setItem(HOME_RI_EXPERIMENT_STATS_KEY, JSON.stringify(stats));
  } catch {
    // noop
  }
}

export function recordHomeRiExperimentMetric(
  variant: HomeRiVariant,
  metric: keyof HomeRiVariantStats,
  delta = 1,
): HomeRiExperimentStats {
  const stats = readHomeRiExperimentStats();
  stats[variant][metric] += delta;
  writeHomeRiExperimentStats(stats);
  return stats;
}

export function resolveHomeRiVariant(userId: string, config: HomeRiExperimentConfig): HomeRiVariant {
  if (!config.enabled) return 'control';
  if (config.forcedVariant) return config.forcedVariant;
  if (!isHomeRiRolloutEligible(userId, config)) return 'control';
  const armBucket = normalizedBucket(`${userId || 'anonymous'}:ri_arm`);
  if (armBucket < 34) return 'control';
  if (armBucket < 67) return 'interactive_v1';
  return 'adaptive_v2';
}

export function isHomeRiRolloutEligible(userId: string, config: HomeRiExperimentConfig): boolean {
  if (!config.enabled) return false;
  return normalizedBucket(userId || 'anonymous') < config.rolloutPct;
}

function completionRate(v: HomeRiVariantStats): number {
  if (v.impressions <= 0) return 0;
  return v.completions / v.impressions;
}

function ratePct(numerator: number, denominator: number): number {
  if (denominator <= 0) return 0;
  return Math.round((numerator / denominator) * 1000) / 10;
}

function weightedLiftPct(control: HomeRiVariantStats, candidate: HomeRiVariantStats): number {
  const controlRate = completionRate(control);
  const candidateRate = completionRate(candidate);
  if (controlRate <= 0) return candidateRate > 0 ? 100 : 0;
  return ((candidateRate - controlRate) / controlRate) * 100;
}

function confidenceScore(control: HomeRiVariantStats, candidate: HomeRiVariantStats): number {
  const sampleBalance = Math.min(control.impressions, candidate.impressions);
  const total = control.impressions + candidate.impressions;
  if (sampleBalance <= 0 || total <= 0) return 0;
  const signal = Math.abs(completionRate(candidate) - completionRate(control));
  return Math.max(0, Math.min(100, Math.round((sampleBalance / total) * 100 + signal * 250)));
}

export function maybeAutoPromoteHomeRiVariant(
  config: HomeRiExperimentConfig,
): { config: HomeRiExperimentConfig; promotedTo: HomeRiVariant | null; stats: HomeRiExperimentStats } {
  const stats = readHomeRiExperimentStats();
  if (!config.enabled || config.forcedVariant) {
    return { config, promotedTo: null, stats };
  }
  const control = stats.control;
  if (control.impressions < config.minImpressionsPerVariant) {
    return { config, promotedTo: null, stats };
  }
  const candidates: HomeRiVariant[] = ['interactive_v1', 'adaptive_v2'];
  let winner: HomeRiVariant | null = null;
  let bestLift = Number.NEGATIVE_INFINITY;
  for (const variant of candidates) {
    const candidate = stats[variant];
    if (candidate.impressions < config.minImpressionsPerVariant) continue;
    const liftPct = weightedLiftPct(control, candidate);
    const confidence = confidenceScore(control, candidate);
    if (liftPct >= config.minLiftPct && confidence >= config.minConfidence && liftPct > bestLift) {
      bestLift = liftPct;
      winner = variant;
    }
  }
  if (!winner) return { config, promotedTo: null, stats };
  const updated: HomeRiExperimentConfig = {
    ...config,
    forcedVariant: winner,
    lastAutoPromotedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  writeHomeRiExperimentConfig(updated);
  return { config: updated, promotedTo: winner, stats };
}

export function getHomeRiExperimentHealth(config: HomeRiExperimentConfig): HomeRiExperimentHealth {
  const stats = readHomeRiExperimentStats();
  const variants = (Object.keys(stats) as HomeRiVariant[]).reduce<Record<HomeRiVariant, HomeRiVariantHealth>>(
    (acc, variant) => {
      const row = stats[variant];
      const impressions = Number(row?.impressions || 0);
      const interactions = Number(row?.interactions || 0);
      const completions = Number(row?.completions || 0);
      const upgradeClicks = Number(row?.upgradeClicks || 0);
      acc[variant] = {
        variant,
        impressions,
        interactions,
        completions,
        upgradeClicks,
        interactionRatePct: ratePct(interactions, impressions),
        completionRatePct: ratePct(completions, impressions),
        upgradeRatePct: ratePct(upgradeClicks, impressions),
      };
      return acc;
    },
    {
      control: {
        variant: 'control',
        impressions: 0,
        interactions: 0,
        completions: 0,
        upgradeClicks: 0,
        interactionRatePct: 0,
        completionRatePct: 0,
        upgradeRatePct: 0,
      },
      interactive_v1: {
        variant: 'interactive_v1',
        impressions: 0,
        interactions: 0,
        completions: 0,
        upgradeClicks: 0,
        interactionRatePct: 0,
        completionRatePct: 0,
        upgradeRatePct: 0,
      },
      adaptive_v2: {
        variant: 'adaptive_v2',
        impressions: 0,
        interactions: 0,
        completions: 0,
        upgradeClicks: 0,
        interactionRatePct: 0,
        completionRatePct: 0,
        upgradeRatePct: 0,
      },
    },
  );

  const control = stats.control;
  const promotion: HomeRiPromotionHealth[] = (['interactive_v1', 'adaptive_v2'] as const).map((variant) => {
    const candidate = stats[variant];
    const liftPct = weightedLiftPct(control, candidate);
    const confidence = confidenceScore(control, candidate);
    const sampleReady =
      control.impressions >= config.minImpressionsPerVariant &&
      candidate.impressions >= config.minImpressionsPerVariant;
    const eligibleForPromotion = sampleReady && liftPct >= config.minLiftPct && confidence >= config.minConfidence;
    return {
      variant,
      liftPct: Math.round(liftPct * 10) / 10,
      confidence,
      sampleReady,
      eligibleForPromotion,
    };
  });

  const totals = (Object.values(variants) as HomeRiVariantHealth[]).reduce(
    (acc, row) => {
      acc.impressions += row.impressions;
      acc.interactions += row.interactions;
      acc.completions += row.completions;
      acc.upgradeClicks += row.upgradeClicks;
      return acc;
    },
    { impressions: 0, interactions: 0, completions: 0, upgradeClicks: 0 },
  );

  return {
    totals,
    variants,
    promotion,
  };
}

export function resetHomeRiExperimentStats(): void {
  try {
    localStorage.setItem(HOME_RI_EXPERIMENT_STATS_KEY, JSON.stringify(defaultStats()));
    const toDelete: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(HOME_RI_ASSIGNMENT_LOG_PREFIX)) {
        toDelete.push(key);
      }
    }
    toDelete.forEach((key) => localStorage.removeItem(key));
  } catch {
    // noop
  }
}

export function getHomeRiRolloutRecommendation(
  config: HomeRiExperimentConfig,
  health: HomeRiExperimentHealth,
): HomeRiRolloutRecommendation {
  const steps = [10, 20, 35, 50];
  const current = config.enabled ? Math.max(0, Math.min(100, config.rolloutPct)) : 0;

  if (!config.enabled || current === 0) {
    return {
      action: 'start',
      targetRolloutPct: 10,
      reason: 'Rollout is off. Start at 10% to gather baseline samples safely.',
    };
  }

  const rollbackCandidate = health.promotion.find(
    (p) => p.sampleReady && p.confidence >= config.minConfidence && p.liftPct <= -10,
  );
  if (rollbackCandidate) {
    return {
      action: 'rollback',
      targetRolloutPct: 0,
      reason: `${rollbackCandidate.variant} shows ${rollbackCandidate.liftPct}% lift at ${rollbackCandidate.confidence}% confidence (degradation guardrail).`,
    };
  }

  if (current >= 50) {
    return {
      action: 'hold',
      targetRolloutPct: 50,
      reason: 'Rollout is already at max operational target (50%). Hold and monitor.',
    };
  }

  const nextStep = steps.find((step) => step > current) ?? current;
  const anyEligible = health.promotion.some((p) => p.eligibleForPromotion);
  if (anyEligible) {
    return {
      action: 'ramp',
      targetRolloutPct: nextStep,
      reason: 'Promotion criteria are met for at least one candidate. Safe to ramp one step.',
    };
  }

  const missingSamples = health.promotion.some((p) => !p.sampleReady);
  if (missingSamples) {
    return {
      action: 'hold',
      targetRolloutPct: current,
      reason: 'Not enough per-arm impressions yet. Hold until sample-ready.',
    };
  }

  return {
    action: 'hold',
    targetRolloutPct: current,
    reason: 'Samples are ready but lift/confidence thresholds are not met. Hold current rollout.',
  };
}

export function getHomeRiRolloutCooldownRemainingMs(nowMs = Date.now()): number {
  try {
    const raw = localStorage.getItem(HOME_RI_ROLLOUT_LAST_CHANGE_AT_KEY);
    const last = raw ? Number.parseInt(raw, 10) : 0;
    if (!Number.isFinite(last) || last <= 0) return 0;
    return Math.max(0, last + HOME_RI_ROLLOUT_COOLDOWN_MS - nowMs);
  } catch {
    return 0;
  }
}

export function isHomeRiRolloutChangeLocked(nowMs = Date.now()): boolean {
  return getHomeRiRolloutCooldownRemainingMs(nowMs) > 0;
}

export function markHomeRiRolloutChange(nowMs = Date.now()): void {
  try {
    localStorage.setItem(HOME_RI_ROLLOUT_LAST_CHANGE_AT_KEY, String(nowMs));
  } catch {
    // noop
  }
}
